// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useMemo, useState } from 'react';
import { StyleSheet, View, useWindowDimensions } from 'react-native';
import { Text } from '@/components/Themed';
import { WEIGHT_STEPS } from '@/components/GameWeightControl';
import RoundGameInfoModal from '@/components/RoundGameInfoModal';
import { useUser } from '@/app/user-context';
import { getGameDisplayName } from '@/features/game-definitions/i18n';
import { formatMeritLadder, getMeritLadder, useGameScoringRules } from '@/features/game-definitions/merits';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import appColors from '@/theme/colors';
import SectionHeading from '../shared/SectionHeading';
import GameTile from '../shared/GameTile';
import MeritDistributionBar from '../shared/MeritDistributionBar';
import type { GameDefinition } from '../../types';
import { normalizeGameId } from '../../utils';

type Props = {
  roundId: string;
  enabledGameDefinitions: GameDefinition[];
  roundGameIds: string[];
  weightsByGameId: Record<string, number>;
  tournamentGameIds: string[];
  tournamentWeightsByGameId: Record<string, number>;
  playerCount: number;
  onUpdateRoundGames: (roundId: string, gameIds: string[]) => void;
  onWeightChange: (roundId: string, gameId: string, weightPercent: number) => void;
};

const FOUR_COLUMN_BREAKPOINT = 1500;
const THREE_COLUMN_BREAKPOINT = 900;
const GAP = 12;
const FALLBACK_MERIT_PLAYER_COUNT = 4;

export default function RoundGamesEditor({
  roundId,
  enabledGameDefinitions,
  roundGameIds,
  weightsByGameId,
  tournamentGameIds,
  tournamentWeightsByGameId,
  playerCount,
  onUpdateRoundGames,
  onWeightChange,
}: Props) {
  const { width: viewportWidth } = useWindowDimensions();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const { user } = useUser();
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoGame, setInfoGame] = useState<GameDefinition | null>(null);

  const roundGameSet = useMemo(() => new Set(roundGameIds.map(normalizeGameId)), [roundGameIds]);
  const tournamentGameSet = useMemo(
    () => new Set(tournamentGameIds.map(normalizeGameId)),
    [tournamentGameIds],
  );
  const meritPlayerCount = playerCount > 1 ? playerCount : FALLBACK_MERIT_PLAYER_COUNT;
  const scoringPointsByGameId = useGameScoringRules(apiBaseUrl, user?.token, meritPlayerCount);

  const getWeightPercent = useCallback(
    (game: GameDefinition) => {
      if (tournamentGameSet.has(normalizeGameId(game.id))) {
        return tournamentWeightsByGameId[game.id] ?? normalizeWeight(game.defaultWeightPercent);
      }
      return weightsByGameId[game.id] ?? normalizeWeight(game.defaultWeightPercent);
    },
    [tournamentGameSet, tournamentWeightsByGameId, weightsByGameId],
  );

  const cycleWeight = useCallback(
    (game: GameDefinition) => {
      if (tournamentGameSet.has(normalizeGameId(game.id))) return;
      const current = getWeightPercent(game);
      const normalized = (WEIGHT_STEPS as readonly number[]).includes(current) ? current : 100;
      const nextIndex = ((WEIGHT_STEPS as readonly number[]).indexOf(normalized) + 1) % WEIGHT_STEPS.length;
      onWeightChange(roundId, game.id, WEIGHT_STEPS[nextIndex]);
    },
    [getWeightPercent, onWeightChange, roundId, tournamentGameSet],
  );

  const meritDistributionEntries = useMemo(
    () =>
      enabledGameDefinitions
        .filter((game) => {
          const normalized = normalizeGameId(game.id);
          return roundGameSet.has(normalized) || tournamentGameSet.has(normalized);
        })
        .map((game) => ({
          gameId: game.id,
          name: getGameDisplayName(game),
          ladder: getMeritLadder(scoringPointsByGameId, game.id, getWeightPercent(game)),
        })),
    [enabledGameDefinitions, getWeightPercent, roundGameSet, scoringPointsByGameId, tournamentGameSet],
  );

  const numColumns = useMemo(() => {
    if (viewportWidth >= FOUR_COLUMN_BREAKPOINT) return 4;
    if (viewportWidth >= THREE_COLUMN_BREAKPOINT) return 3;
    return 2;
  }, [viewportWidth]);

  const rows = useMemo(() => {
    const out: GameDefinition[][] = [];
    for (let i = 0; i < enabledGameDefinitions.length; i += numColumns) {
      out.push(enabledGameDefinitions.slice(i, i + numColumns));
    }
    return out;
  }, [enabledGameDefinitions, numColumns]);

  const handleToggle = useCallback(
    (gameId: string) => {
      const normalizedId = normalizeGameId(gameId);
      if (tournamentGameSet.has(normalizedId)) return;

      const currentRoundOnly = roundGameIds.filter((id) => !tournamentGameSet.has(normalizeGameId(id)));
      if (roundGameSet.has(normalizedId)) {
        onUpdateRoundGames(
          roundId,
          currentRoundOnly.filter((id) => normalizeGameId(id) !== normalizedId),
        );
      } else {
        onUpdateRoundGames(roundId, [...currentRoundOnly, gameId]);
      }
    },
    [roundId, roundGameIds, roundGameSet, tournamentGameSet, onUpdateRoundGames],
  );

  const openInfo = useCallback((game: GameDefinition) => {
    setInfoGame(game);
    setInfoOpen(true);
  }, []);

  return (
    <View style={styles.container}>
      <SectionHeading title="Round Games" count={roundGameIds.length} />
      <Text style={styles.hint}>
        Tour games are auto-included and cannot be removed. Additional games can be toggled.
      </Text>
      <MeritDistributionBar entries={meritDistributionEntries} />
      {enabledGameDefinitions.length === 0 ? (
        <Text style={styles.emptyText}>No games available</Text>
      ) : (
        <View style={styles.grid}>
          {rows.map((row, rowIndex) => (
            <View key={rowIndex} style={styles.row}>
              {row.map((game) => {
                const normalized = normalizeGameId(game.id);
                const isTourGame = tournamentGameSet.has(normalized);
                const isInRound = roundGameSet.has(normalized);
                const isSelected = isTourGame || isInRound;
                const weightPercent = getWeightPercent(game);
                const ladder = isSelected ? getMeritLadder(scoringPointsByGameId, game.id, weightPercent) : [];
                return (
                  <GameTile
                    key={game.id}
                    game={game}
                    selected={isSelected}
                    locked={isTourGame}
                    lockedLabel={isTourGame ? 'Tour' : undefined}
                    onPress={() => handleToggle(game.id)}
                    onInfoPress={() => openInfo(game)}
                    meritLadderLabel={ladder.length > 0 ? formatMeritLadder(ladder) : undefined}
                    perTeam={(game.competitionType ?? '').toLowerCase() === 'team'}
                    weightPercent={weightPercent}
                    onWeightPress={() => cycleWeight(game)}
                    weightLocked={isTourGame}
                  />
                );
              })}
              {Array.from({ length: numColumns - row.length }, (_, i) => (
                <View key={`filler-${i}`} style={styles.filler} />
              ))}
            </View>
          ))}
        </View>
      )}
      <RoundGameInfoModal
        visible={infoOpen}
        game={infoGame}
        apiBaseUrl={apiBaseUrl}
        onClose={() => setInfoOpen(false)}
      />
    </View>
  );
}

function normalizeWeight(value: number | null | undefined): number {
  if (typeof value !== 'number' || Number.isNaN(value)) return 100;
  return Math.max(0, Math.min(100, Math.round(value)));
}

const styles = StyleSheet.create({
  container: {
    gap: 8,
  },
  hint: {
    fontSize: 12,
    color: appColors.text.muted,
    fontStyle: 'italic',
    paddingHorizontal: 4,
  },
  grid: {
    gap: GAP,
  },
  row: {
    flexDirection: 'row',
    gap: GAP,
    alignItems: 'stretch',
  },
  filler: {
    flex: 1,
  },
  emptyText: {
    fontSize: 14,
    color: appColors.text.muted,
    fontStyle: 'italic',
    paddingVertical: 12,
  },
});
